/**
 * Date Utilities
 *
 * Centralized date/time formatting and parsing.
 *
 * CRITICAL: This application uses ONLY these formats:
 * - Date: dd-MM-yyyy (e.g., 22-01-2026)
 * - DateTime: dd-MM-yyyy HH:mm:ss (e.g., 22-01-2026 14:30:00)
 *
 * All date operations MUST use these utilities to ensure consistency.
 */

import { format, parse, isValid, parseISO, isDate } from 'date-fns';

// Standard date formats used throughout the application
export const DATE_FORMAT = 'dd-MM-yyyy';
export const DATE_TIME_FORMAT = 'dd-MM-yyyy HH:mm:ss';
export const TIME_FORMAT = 'HH:mm:ss';

// ISO format for API communication (if needed)
export const ISO_DATE_FORMAT = 'yyyy-MM-dd';

/**
 * Converts a value to a Date object
 * Handles Date objects, ISO strings, and our custom format strings
 */
function toDateObject(date: Date | string | null | undefined): Date | null {
  if (!date) {
    return null;
  }

  if (isDate(date)) {
    return date as Date;
  }

  if (typeof date === 'string') {
    // Try parsing as ISO string first
    const isoDate = parseISO(date);
    if (isValid(isoDate)) {
      return isoDate;
    }

    // Try parsing as our custom format
    const customDate = parse(date, DATE_FORMAT, new Date());
    if (isValid(customDate)) {
      return customDate;
    }

    // Try parsing as our custom datetime format
    const customDateTime = parse(date, DATE_TIME_FORMAT, new Date());
    if (isValid(customDateTime)) {
      return customDateTime;
    }
  }

  return null;
}

/**
 * Format a date to dd-MM-yyyy
 *
 * @param date - Date object, ISO string, or our custom format string
 * @returns Formatted date string (dd-MM-yyyy) or empty string if invalid
 *
 * @example
 * formatDate(new Date(2026, 0, 22)) // "22-01-2026"
 * formatDate("2026-01-22T10:30:00Z") // "22-01-2026"
 * formatDate(null) // ""
 */
export function formatDate(date: Date | string | null | undefined): string {
  const dateObj = toDateObject(date);

  if (!dateObj || !isValid(dateObj)) {
    return '';
  }

  return format(dateObj, DATE_FORMAT);
}

/**
 * Format a date to dd-MM-yyyy HH:mm:ss
 *
 * @param date - Date object, ISO string, or our custom format string
 * @returns Formatted datetime string (dd-MM-yyyy HH:mm:ss) or empty string if invalid
 *
 * @example
 * formatDateTime(new Date(2026, 0, 22, 14, 30, 45)) // "22-01-2026 14:30:45"
 * formatDateTime("2026-01-22T14:30:45Z") // "22-01-2026 14:30:45"
 */
export function formatDateTime(date: Date | string | null | undefined): string {
  const dateObj = toDateObject(date);

  if (!dateObj || !isValid(dateObj)) {
    return '';
  }

  return format(dateObj, DATE_TIME_FORMAT);
}

/**
 * Format a date to HH:mm:ss
 *
 * @param date - Date object, ISO string, or our custom format string
 * @returns Formatted time string (HH:mm:ss) or empty string if invalid
 */
export function formatTime(date: Date | string | null | undefined): string {
  const dateObj = toDateObject(date);

  if (!dateObj || !isValid(dateObj)) {
    return '';
  }

  return format(dateObj, TIME_FORMAT);
}

/**
 * Parse a dd-MM-yyyy string to Date
 *
 * @param dateString - String in dd-MM-yyyy format
 * @returns Date object or null if invalid
 *
 * @example
 * parseDate("22-01-2026") // Date object for Jan 22, 2026
 * parseDate("invalid") // null
 */
export function parseDate(dateString: string): Date | null {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }

  const parsed = parse(dateString.trim(), DATE_FORMAT, new Date());

  return isValid(parsed) ? parsed : null;
}

/**
 * Parse a dd-MM-yyyy HH:mm:ss string to Date
 *
 * @param dateString - String in dd-MM-yyyy HH:mm:ss format
 * @returns Date object or null if invalid
 *
 * @example
 * parseDateTime("22-01-2026 14:30:45") // Date object
 * parseDateTime("invalid") // null
 */
export function parseDateTime(dateString: string): Date | null {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }

  const parsed = parse(dateString.trim(), DATE_TIME_FORMAT, new Date());

  return isValid(parsed) ? parsed : null;
}

/**
 * Check if a string is a valid date in dd-MM-yyyy format
 *
 * @param dateString - String to validate
 * @returns true if valid dd-MM-yyyy format
 *
 * @example
 * isValidDateString("22-01-2026") // true
 * isValidDateString("2026-01-22") // false (wrong format)
 * isValidDateString("32-01-2026") // false (invalid day)
 */
export function isValidDateString(dateString: string): boolean {
  if (!dateString || typeof dateString !== 'string') {
    return false;
  }

  // Check format with regex first
  const dateRegex = /^\d{2}-\d{2}-\d{4}$/;
  if (!dateRegex.test(dateString.trim())) {
    return false;
  }

  // Then validate the actual date
  const parsed = parse(dateString.trim(), DATE_FORMAT, new Date());
  return isValid(parsed);
}

/**
 * Check if a string is a valid datetime in dd-MM-yyyy HH:mm:ss format
 *
 * @param dateString - String to validate
 * @returns true if valid dd-MM-yyyy HH:mm:ss format
 */
export function isValidDateTimeString(dateString: string): boolean {
  if (!dateString || typeof dateString !== 'string') {
    return false;
  }

  // Check format with regex first
  const dateTimeRegex = /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/;
  if (!dateTimeRegex.test(dateString.trim())) {
    return false;
  }

  // Then validate the actual datetime
  const parsed = parse(dateString.trim(), DATE_TIME_FORMAT, new Date());
  return isValid(parsed);
}

/**
 * Convert a date to ISO string for API requests
 *
 * @param date - Date object or string in our format
 * @returns ISO date string (yyyy-MM-dd) or empty string if invalid
 */
export function toISODateString(date: Date | string | null | undefined): string {
  const dateObj = toDateObject(date);

  if (!dateObj || !isValid(dateObj)) {
    return '';
  }

  return format(dateObj, ISO_DATE_FORMAT);
}

/**
 * Convert a date to ISO datetime string for API requests
 *
 * @param date - Date object or string in our format
 * @returns ISO datetime string or empty string if invalid
 */
export function toISOString(date: Date | string | null | undefined): string {
  const dateObj = toDateObject(date);

  if (!dateObj || !isValid(dateObj)) {
    return '';
  }

  return dateObj.toISOString();
}

/**
 * Get the current date formatted as dd-MM-yyyy
 */
export function getCurrentDate(): string {
  return format(new Date(), DATE_FORMAT);
}

/**
 * Get the current datetime formatted as dd-MM-yyyy HH:mm:ss
 */
export function getCurrentDateTime(): string {
  return format(new Date(), DATE_TIME_FORMAT);
}

/**
 * Compare two dates (ignoring time)
 *
 * @returns -1 if date1 < date2, 0 if equal, 1 if date1 > date2
 */
export function compareDates(
  date1: Date | string | null | undefined,
  date2: Date | string | null | undefined
): number {
  const d1 = toDateObject(date1);
  const d2 = toDateObject(date2);

  if (!d1 && !d2) return 0;
  if (!d1) return -1;
  if (!d2) return 1;

  const d1Str = format(d1, DATE_FORMAT);
  const d2Str = format(d2, DATE_FORMAT);

  if (d1Str < d2Str) return -1;
  if (d1Str > d2Str) return 1;
  return 0;
}

/**
 * Check if a date is today
 */
export function isToday(date: Date | string | null | undefined): boolean {
  const dateObj = toDateObject(date);
  if (!dateObj) return false;

  return format(dateObj, DATE_FORMAT) === format(new Date(), DATE_FORMAT);
}

/**
 * Get relative time description (e.g., "2 hours ago", "3 days ago")
 * Falls back to formatted date for older dates
 */
export function getRelativeTime(date: Date | string | null | undefined): string {
  const dateObj = toDateObject(date);
  if (!dateObj) return '';

  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;

  return formatDate(dateObj);
}
